import { FieldErrors, UseFormSetValue } from 'react-hook-form';
import { CaseQueueScheduleRequest, MONTHS, ProviderCaseQueue } from 'type-declarations';
import { FormData } from './constants';
import dayjs from 'dayjs';

// Uses react-hook-form setValue() to restore the form state
export const setFormFields = (
	selectedRecord: CaseQueueScheduleRequest | ProviderCaseQueue,
	setValue: UseFormSetValue<FormData>
): void => {
	const [startHour, startMin] = selectedRecord.startTime.split(':');
	const [endHour, endMin] = selectedRecord.endTime.split(':');

	const startTime = dayjs(selectedRecord.startDate)
		.hour(Number(startHour))
		.minute(Number(startMin));
	const endTime = dayjs(selectedRecord.endDate).hour(Number(endHour)).minute(Number(endMin));

	const fStartTime = startTime.format();
	const fEndTime = endTime.format();

	setValue('startTime', fStartTime);
	setValue('endTime', fEndTime);
	setValue('recurrenceType', selectedRecord.recurrenceType);
	setValue('description', selectedRecord.description);
	setValue('startDate', selectedRecord.startDate);
	setValue('endDate', selectedRecord.endDate);
	setValue('dayOfWeek', selectedRecord.dayOfWeek || '');
	setValue('includeWeekends', selectedRecord.includeWeekends || false);
	setValue('dayOfMonth', selectedRecord.dayOfMonth || '');
	setValue('month', selectedRecord.month || '');
};

// Validates all reqs & checks against zod errors before enabling dialog submit button
export const checkIsDisabled = (formWatch: FormData, errors: FieldErrors<FormData>): boolean => {
	const zodErrors = Object.keys(errors).length;
	const formRequiredInputs = formWatch.startTime === '' || formWatch.endTime === '';
	if (zodErrors === 0) {
		switch (formWatch.recurrenceType) {
			case 'SINGLE':
				return formRequiredInputs || formWatch.startDate === '' || formWatch.endDate === '';
			case 'DAILY':
				return formRequiredInputs;
			case 'WEEKLY':
				return formRequiredInputs || formWatch.dayOfWeek === '';
			case 'MONTHLY':
				return formRequiredInputs || formWatch.dayOfMonth === '';
			case 'YEARLY':
				return formRequiredInputs || formWatch.dayOfMonth === '' || formWatch.month === '';
		}
	}
	return true;
};

// Form submission, if there's an existing ID, performs PUT. If absent, performs POST
export const handleSubmission = (
	formWatch: FormData,
	selectedRecord: ProviderCaseQueue,
	createAction: (queue: CaseQueueScheduleRequest) => void,
	editAction: (queue: CaseQueueScheduleRequest, id: string) => void,
	reset
): void => {
	if (selectedRecord?.id) {
		const payload: ProviderCaseQueue = {
			feed: selectedRecord.feed,
			description: formWatch.description || selectedRecord.description,
			id: selectedRecord.id,
			startTime: formatTime(formWatch.startTime!) || selectedRecord.startTime,
			endTime: formatTime(formWatch.endTime!) || selectedRecord.endTime,
			recurrenceType: formWatch.recurrenceType || selectedRecord.recurrenceType,
			dayOfWeek: formWatch.dayOfWeek || selectedRecord.dayOfWeek,
			includeWeekends: formWatch.includeWeekends || null,
			dayOfMonth: formWatch.dayOfMonth || selectedRecord.dayOfMonth,
			month: formWatch.month || selectedRecord.month,
		};
		if (formWatch.recurrenceType === 'SINGLE') {
			payload.startDate = formWatch.startDate;
			payload.endDate = formWatch.endDate;
		}
		editAction(payload, selectedRecord.id);
	} else {
		const payload: CaseQueueScheduleRequest = {
			startTime: formatTime(formWatch.startTime!),
			endTime: formatTime(formWatch.endTime!),
			recurrenceType: formWatch.recurrenceType,
			description: formWatch.description,
			dayOfWeek: formWatch.dayOfWeek ? formWatch.dayOfWeek : null,
			includeWeekends: formWatch.includeWeekends ? formWatch.includeWeekends : null,
			dayOfMonth: formWatch.dayOfMonth ? formWatch.dayOfMonth : null,
			month: formWatch.month ? formWatch.month : null,
		};
		if (formWatch.recurrenceType === 'SINGLE') {
			payload.startDate = formWatch.startDate;
			payload.endDate = formWatch.endDate;
		}
		createAction(payload);
	}
	reset();
};

// Returns string formatted as 'HH:MM', matching expectation of backend
export const formatTime = (time: string): string => {
	const t = dayjs(time);
	return `${String(t.hour()).padStart(2, '0')}:${String(t.minute()).padStart(2, '0')}`;
};

// Formats start/end time header column values, which depend on recurrence type & possibly other variables
export const getFormattedColumnTime = (params, key: 'startTime' | 'endTime'): string => {
	switch (params.row.recurrenceType) {
		case 'SINGLE':
			if (key === 'startTime') return `${params.row.startDate.split('T')[0]} ${params.row[key]}`;
			else return `${params.row.endDate.split('T')[0]} ${params.row[key]}`;
		case 'DAILY':
			return `${params.row.includeWeekends ? 'Everyday' : 'Weekdays'} at ${params.row[key]}`;
		case 'WEEKLY':
			return `${params.row.dayOfWeek} at ${params.row[key]}`;
		case 'MONTHLY':
			return `Day ${params.row.dayOfMonth} at ${params.row[key]}`;
		case 'YEARLY':
			return `${MONTHS[params.row.month - 1]} ${params.row.dayOfMonth} at ${params.row[key]}`;
		default:
			return '';
	}
};
